// Store local de lembretes de programas ("A seguir" → 🔔).
//
// localStorage simples, sobrevive logout (não tem dado pessoal — só
// IDs de programa + canal). Usado na LiveScreen pra:
//   1. Botão tier-cíclico (15min → 5min → agora → off) no card "A seguir".
//   2. Banner top quando lembrete dispara (poll 30s) — texto contextual
//      pelo tier ("Começa em 15 min" / "5 min" / "Agora").
//
// Tier define quantos minutos ANTES do programa o banner aparece.

const STORE_KEY = 'iptv:reminders'

/** Tier de antecedência. 'off' = sem lembrete (nada salvo). */
export type ReminderTier = '15min' | '5min' | 'now'

const TIER_OFFSET_MS: Record<ReminderTier, number> = {
  '15min': 15 * 60 * 1000,
  '5min': 5 * 60 * 1000,
  now: 0,
}

/** Ciclo: off → 15min → 5min → now → off. Usado pra clique do botão. */
export function nextTier(current: ReminderTier | null): ReminderTier | null {
  if (current === null) return '15min'
  if (current === '15min') return '5min'
  if (current === '5min') return 'now'
  return null // de 'now' volta pra off
}

export interface Reminder {
  id: string // hash de channelId+programTitle+startsAt
  channelId: string
  channelName: string
  programTitle: string
  startsAt: number // unix ms — quando o programa começa
  tier: ReminderTier // quantos minutos antes disparar
  createdAt: number // unix ms
  notified?: boolean // banner já mostrado
}

function load(): Reminder[] {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as Reminder[]
  } catch {
    return []
  }
}

function save(list: Reminder[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(list))
  } catch {
    // quota/dom exception — silently drop
  }
}

/** Computa id estável (mesma combinação produz mesmo id pra evitar
 *  duplicação quando user alterna 🔔 rápido). Hash simples DJB2 — não
 *  precisa de crypto. */
export function reminderId(
  channelId: string,
  programTitle: string,
  startsAt: number,
): string {
  const input = `${channelId}::${programTitle}::${startsAt}`
  let hash = 5381
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0
  }
  return `r${(hash >>> 0).toString(36)}`
}

/** Lista todos os lembretes ativos (não-expirados). Limpa expirados. */
export function listReminders(): Reminder[] {
  const now = Date.now()
  const all = load()
  const active = all.filter((r) => r.startsAt + 5 * 60 * 1000 > now) // 5min tolerância
  if (active.length !== all.length) save(active)
  return active
}

/** Tier do lembrete pra um programa, ou null se não tem lembrete. */
export function getReminderTier(
  channelId: string,
  programTitle: string,
  startsAt: number,
): ReminderTier | null {
  const id = reminderId(channelId, programTitle, startsAt)
  const r = load().find((x) => x.id === id)
  return r?.tier ?? null
}

/** Existe lembrete em qualquer tier (compat com chamadas antigas). */
export function hasReminder(
  channelId: string,
  programTitle: string,
  startsAt: number,
): boolean {
  return getReminderTier(channelId, programTitle, startsAt) !== null
}

/**
 * Avança o tier no clique. off → 15min → 5min → agora → off.
 * Retorna o novo tier (null = desligado).
 */
export function cycleReminder(input: {
  channelId: string
  channelName: string
  programTitle: string
  startsAt: number
}): ReminderTier | null {
  const id = reminderId(input.channelId, input.programTitle, input.startsAt)
  const all = load()
  const existing = all.find((r) => r.id === id)
  const next = nextTier(existing?.tier ?? null)

  if (next === null) {
    // Desliga: remove
    save(all.filter((r) => r.id !== id))
    return null
  }

  if (existing) {
    // Atualiza tier (e reseta notified pra disparar de novo na nova janela)
    const updated = all.map((r) =>
      r.id === id ? { ...r, tier: next, notified: false } : r,
    )
    save(updated)
    return next
  }

  // Novo
  const reminder: Reminder = {
    id,
    channelId: input.channelId,
    channelName: input.channelName,
    programTitle: input.programTitle,
    startsAt: input.startsAt,
    tier: next,
    createdAt: Date.now(),
  }
  save([...all, reminder])
  return next
}

export function removeReminder(id: string): void {
  const all = load()
  save(all.filter((r) => r.id !== id))
}

/**
 * Lembretes que devem disparar AGORA: já passaram do "fire time"
 * (startsAt - tier offset) e ainda estão na janela de 60s. Marca
 * notified=true pra não disparar de novo.
 */
export function consumeFiringReminders(): Reminder[] {
  const now = Date.now()
  const all = load()
  const firing = all.filter((r) => {
    if (r.notified) return false
    const fireAt = r.startsAt - TIER_OFFSET_MS[r.tier]
    return fireAt <= now && fireAt + 60 * 1000 > now
  })
  if (firing.length === 0) return []
  const updated = all.map((r) =>
    firing.some((f) => f.id === r.id) ? { ...r, notified: true } : r,
  )
  save(updated)
  return firing
}

/** Limpa tudo (usado em hard reset). */
export function clearReminders(): void {
  try {
    localStorage.removeItem(STORE_KEY)
  } catch {
    // ignora
  }
}
